import { FC, useCallback, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import NotificationsIcon from 'mdi-react/NotificationsIcon';
import classNames from 'classnames';

const Notifications = dynamic(
  () => import(/* webpackChunkName: "Notifications" */ 'modules/users/components/Notifications')
);
import { clientNotificationsSelector, proNotificationsSelector } from 'modules/users/duck';
import { useClickOutsideElement } from 'src/hooks';
import type { UserNotification } from 'modules/users/types';

import styles from './NotificationContainer.module.scss';

interface Props {
  isPro?: boolean;
}

const NotificationContainer: FC<Props> = ({ isPro }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const proNotifications = useSelector(proNotificationsSelector) as Array<UserNotification>;
  const clientNotifications = useSelector(clientNotificationsSelector) as Array<UserNotification>;

  let unreadCount = 0;
  if (isPro) {
    unreadCount = proNotifications.filter((notif) => !notif.isRead).length;
  } else {
    unreadCount = clientNotifications.filter((notif) => !notif.isRead).length;
  }

  const unreadLimit = 9;
  const isMoreThanLimit = unreadCount > unreadLimit;

  const container = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    setIsOpen((value) => !value);
  }, []);
  useClickOutsideElement(container, handleToggle, isOpen);

  return (
    <div className={styles.container} ref={container}>
      <button
        className={classNames(styles.button, { [styles.open]: isOpen })}
        onClick={handleToggle}
      >
        {isPro && <NotificationsIcon />}
        {!isPro && (
          <img
            className={styles['icon-mail']}
            alt="icon mail"
            src="/static/images/icons/icon-mail.svg"
          />
        )}
        {unreadCount > 0 && (
          <span className={styles['unread-count-badge']}>
            <span
              className={classNames(styles['unread-count'], { [styles.more]: isMoreThanLimit })}
            >
              {isMoreThanLimit ? '+9' : unreadCount}
            </span>
          </span>
        )}
      </button>
      {isOpen && (
        <div className={styles.popup}>
          <Notifications language={language} />
        </div>
      )}
    </div>
  );
};

export default NotificationContainer;
